import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  AlertCircle, 
  ArrowLeft, 
  Building2, 
  FileText, 
  Copy, 
  Check, 
  Mail, 
  Phone,
  HelpCircle,
  Clock,
  CreditCard,
  ChevronRight
} from 'lucide-react';

const AlternativePaymentOptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get plan details from navigation state
  const planDetails = location?.state || {
    planName: 'Pro Plan',
    price: 29.99,
    interval: 'month'
  };

  // State management
  const [copiedField, setCopiedField] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [invoiceEmail, setInvoiceEmail] = useState(user?.email || '');
  const [invoiceRequested, setInvoiceRequested] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');

  // Generate unique reference number on mount
  useEffect(() => {
    const generateReference = () => {
      const timestamp = Date.now()?.toString()?.slice(-8);
      const random = Math.random()?.toString(36)?.substring(2, 6)?.toUpperCase();
      return `PAY-${timestamp}-${random}`;
    };
    setReferenceNumber(generateReference());
  }, []);

  // Copy to clipboard functionality
  const copyToClipboard = (text, field) => {
    navigator?.clipboard?.writeText(text)?.then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // Handle invoice request
  const handleRequestInvoice = () => {
    if (invoiceEmail?.trim()) {
      setInvoiceRequested(true);
      // In a real application, this would send a request to the backend
      setTimeout(() => {
        setInvoiceRequested(false);
      }, 3000);
    }
  };

  // Bank transfer details
  const bankDetails = {
    bankName: 'First National Bank',
    accountName: 'Vibe Coding Inc.',
    accountNumber: '1234567890',
    routingNumber: '123456789',
    swiftCode: 'FNBAUS33',
    reference: referenceNumber
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/checkout', { state: planDetails })}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Standard Checkout
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Notice Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6 mb-8 shadow-sm">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                Temporary Payment Service Issue
              </h3>
              <p className="text-yellow-800 mb-2">
                We're experiencing temporary technical difficulties with our standard payment processor. 
                We apologize for the inconvenience and are working to restore service as quickly as possible.
              </p>
              <p className="text-yellow-800 font-medium">
                Please use one of the alternative payment methods below to complete your subscription.
              </p>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Your Subscription</h2>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-lg font-semibold text-gray-900">{planDetails?.planName}</p>
              <p className="text-sm text-gray-500">Billed {planDetails?.interval}ly</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-indigo-600">${planDetails?.price}</p>
              <p className="text-sm text-gray-500">per {planDetails?.interval}</p>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="space-y-6">
          {/* Bank Transfer Option */}
          <div 
            className={`bg-white rounded-2xl shadow-lg border-2 transition-all cursor-pointer ${
              selectedMethod === 'bank' ?'border-indigo-500 shadow-xl' :'border-gray-100 hover:border-indigo-200'
            }`}
            onClick={() => setSelectedMethod(selectedMethod === 'bank' ? null : 'bank')}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                    <Building2 className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Bank Transfer / Wire</h3>
                    <p className="text-sm text-gray-500 mt-1">Direct bank payment</p>
                  </div>
                </div>
                <ChevronRight className={`w-6 h-6 text-gray-400 transition-transform ${selectedMethod === 'bank' ? 'rotate-90' : ''}`} />
              </div>

              {selectedMethod === 'bank' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-indigo-50 rounded-xl p-4 mb-6">
                    <div className="flex items-center text-indigo-900 mb-2">
                      <Clock className="w-5 h-5 mr-2" />
                      <span className="font-semibold">Processing Time: 1-3 Business Days</span>
                    </div>
                    <p className="text-sm text-indigo-800">
                      Your account will be activated automatically once payment is received and verified.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Banking Details</h4>
                    
                    {/* Bank Details Grid */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Bank Name</label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{bankDetails?.bankName}</span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              copyToClipboard(bankDetails?.bankName, 'bankName');
                            }}
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            {copiedField === 'bankName' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Account Name</label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{bankDetails?.accountName}</span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              copyToClipboard(bankDetails?.accountName, 'accountName');
                            }}
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            {copiedField === 'accountName' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Account Number</label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{bankDetails?.accountNumber}</span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              copyToClipboard(bankDetails?.accountNumber, 'accountNumber');
                            }}
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            {copiedField === 'accountNumber' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Routing Number</label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{bankDetails?.routingNumber}</span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              copyToClipboard(bankDetails?.routingNumber, 'routingNumber');
                            }}
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            {copiedField === 'routingNumber' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-medium text-gray-600 block mb-1">SWIFT Code</label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{bankDetails?.swiftCode}</span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              copyToClipboard(bankDetails?.swiftCode, 'swiftCode');
                            }}
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            {copiedField === 'swiftCode' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="bg-indigo-100 rounded-lg p-4 border-2 border-indigo-300">
                        <label className="text-xs font-medium text-indigo-900 block mb-1">
                          Reference Number (IMPORTANT)
                        </label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-indigo-900">{bankDetails?.reference}</span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              copyToClipboard(bankDetails?.reference, 'reference');
                            }}
                            className="text-indigo-700 hover:text-indigo-800"
                          >
                            {copiedField === 'reference' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Transfer Instructions */}
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mt-6">
                      <h5 className="font-semibold text-yellow-900 mb-2">Important Instructions</h5>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-yellow-800">
                        <li>Include the reference number <strong>{bankDetails?.reference}</strong> in your transfer</li>
                        <li>Transfer exactly <strong>${planDetails?.price}</strong> to avoid processing delays</li>
                        <li>International transfers may take 3-5 business days</li>
                        <li>You'll receive an email confirmation once payment is verified</li>
                        <li>Your subscription will activate automatically upon payment confirmation</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Payment Option */}
          <div 
            className={`bg-white rounded-2xl shadow-lg border-2 transition-all cursor-pointer ${
              selectedMethod === 'invoice' ?'border-indigo-500 shadow-xl' :'border-gray-100 hover:border-indigo-200'
            }`}
            onClick={() => setSelectedMethod(selectedMethod === 'invoice' ? null : 'invoice')}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Request Invoice</h3>
                    <p className="text-sm text-gray-500 mt-1">Get a formal invoice via email</p>
                  </div>
                </div>
                <ChevronRight className={`w-6 h-6 text-gray-400 transition-transform ${selectedMethod === 'invoice' ? 'rotate-90' : ''}`} />
              </div>

              {selectedMethod === 'invoice' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-purple-50 rounded-xl p-4 mb-6">
                    <div className="flex items-center text-purple-900 mb-2">
                      <Clock className="w-5 h-5 mr-2" />
                      <span className="font-semibold">Payment Terms: NET 30</span>
                    </div>
                    <p className="text-sm text-purple-800">
                      Invoice will be sent within 24 hours. Payment due within 30 days of invoice date.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="invoiceEmail" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address for Invoice
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          id="invoiceEmail"
                          type="email"
                          value={invoiceEmail}
                          onChange={(e) => setInvoiceEmail(e?.target?.value)}
                          placeholder="your@email.com"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                          disabled={invoiceRequested}
                        />
                      </div>
                    </div>

                    {invoiceRequested ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center text-green-800">
                          <Check className="w-5 h-5 mr-2" />
                          <span className="font-medium">Invoice request submitted successfully!</span>
                        </div>
                        <p className="text-sm text-green-700 mt-2">
                          You'll receive the invoice at <strong>{invoiceEmail}</strong> within 24 hours.
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleRequestInvoice();
                        }}
                        disabled={!invoiceEmail?.trim()}
                        className="w-full bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Request Invoice
                      </button>
                    )}

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">Accepted Payment Methods</h5>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                        <div className="flex items-center">
                          <CreditCard className="w-4 h-4 mr-2 text-gray-500" />
                          <span>Check</span>
                        </div>
                        <div className="flex items-center">
                          <CreditCard className="w-4 h-4 mr-2 text-gray-500" />
                          <span>Wire Transfer</span>
                        </div>
                        <div className="flex items-center">
                          <CreditCard className="w-4 h-4 mr-2 text-gray-500" />
                          <span>ACH Transfer</span>
                        </div>
                        <div className="flex items-center">
                          <CreditCard className="w-4 h-4 mr-2 text-gray-500" />
                          <span>Bank Draft</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-8 border border-gray-100">
          <div className="flex items-center mb-4">
            <HelpCircle className="w-6 h-6 text-indigo-600 mr-3" />
            <h3 className="text-xl font-bold text-gray-900">Need Help?</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Our support team is here to assist you with your payment. Contact us for any questions or issues.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-indigo-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Email Support</p>
                <a href="mailto:billing@vibecoding.com" className="text-sm text-indigo-600 hover:text-indigo-700">
                  billing@vibecoding.com
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <Phone className="w-5 h-5 text-indigo-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Phone Support</p>
                <a href="tel:+1-800-123-4567" className="text-sm text-indigo-600 hover:text-indigo-700">
                  +1 (800) 123-4567
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlternativePaymentOptions;